import { z } from "zod";

/**
 * These types represent data from the import pipeline API responses. They use PascalCase property names to match the C# API conventions.
 */

const dimensionsImportPascalSchema = z.object({
	Length: z.number(),
	Width: z.number(),
	Height: z.number(),
});

const mediaImportPascalSchema = z.object({
	Style: z.string(),
	Brand: z.string().nullable().optional(),
	Dimensions: dimensionsImportPascalSchema.optional(),
	Thickness: z.number().optional(),
	Attributes: z.record(z.string(), z.string()).optional(),
});

const mediaWithDimensionsImportPascalSchema = mediaImportPascalSchema.extend({
	Dimensions: dimensionsImportPascalSchema,
	Thickness: z.number(),
	Attributes: z.record(z.string(), z.string()),
});

const labelImportPascalSchema = z.object({
	Media: mediaImportPascalSchema,
	VerificationCode: z.string().nullable().optional(),
	Value: z.string().nullable().optional(),
});
export type LabelImportPascal = z.infer<typeof labelImportPascalSchema>;

const contentImportPascalSchema = z.object({
	Dimensions: dimensionsImportPascalSchema,
	Weight: z.number(),
	Fragility: z.string().nullable().optional(),
	UniqueId: z.string(),
	Identifier: z.string(),
	Quantity: z.number(),
	Attributes: z.record(z.string(), z.string()).optional(),
});

const cartonImportPascalSchema = z.object({
	Media: mediaWithDimensionsImportPascalSchema,
	Rotation: z.string().nullable().optional(),
	Attributes: z.record(z.string(), z.string()),
	Labels: z.array(labelImportPascalSchema),
	Contents: z.array(contentImportPascalSchema).nullable().optional(),
});
export type CartonImportPascal = z.infer<typeof cartonImportPascalSchema>;

const locationImportPascalSchema = z.object({
	Machine: z.string().nullable().optional(),
	MachineGroup: z.string().nullable().optional(),
	ProductionGroup: z.string().nullable().optional(),
	PackStation: z.string().nullable().optional(),
	PickZone: z.string().nullable().optional(),
});

const optionsImportPascalSchema = z.object({
	PrioritizeProductionBy: z.string().nullable().optional(),
});

export type ContentImportPascal = z.infer<typeof contentImportPascalSchema>;

const solutionImportPascalSchema = z.object({
	Alias: z.string(),
	LicensePlateNumber: z.string().nullable().optional(),
	InternalUniqueId: z.string(),
	Quantity: z.number(),
	Priority: z.number().optional(),
	GroupIdDate: z.string().nullable().optional(),
	CreateByDate: z.string().nullable().optional(),
	errorType: z.string().nullable().optional(),
	Location: locationImportPascalSchema.nullable().optional(),
	Options: optionsImportPascalSchema.nullable().optional(),
	Attributes: z.record(z.string(), z.string()).nullable().optional(),
	Labels: z.array(labelImportPascalSchema).optional(),
	Cartons: z.array(cartonImportPascalSchema).optional(),
	CartonPropertyGroups: z.array(z.string()).optional(),
	Classification: z.string().nullable().optional(),
	WaveId: z.string().nullable().optional(),
});
export type SolutionImportPascal = z.infer<typeof solutionImportPascalSchema>;

const _testResultImportPascalSchema = solutionImportPascalSchema.extend({
	ImportLog: z.array(z.any()), // ImportLogPascal from import-log.ts
	errorType: z.string().optional(),
});
export type TestResultImportPascal = z.infer<typeof _testResultImportPascalSchema>;

const dependencyItemSchema = z.object({
	id: z.string(),
	alias: z.string(),
	name: z.string().optional(),
});
export type DependencyItem = z.infer<typeof dependencyItemSchema>;

const _solutionPreviewDependenciesSchema = z.object({
	serverSettings: z
		.object({
			unitOfMeasure: z.string().optional(),
		})
		.passthrough()
		.optional(),
	printTemplates: z.array(z.any()).optional(),
	packagingDesigns: z.array(z.any()).optional(),
	machines: z.array(dependencyItemSchema).optional(),
	machineGroups: z.array(dependencyItemSchema).optional(),
	productionGroups: z.array(dependencyItemSchema).optional(),
});

export type SolutionPreviewDependencies = z.infer<typeof _solutionPreviewDependenciesSchema>;
